// iter59 · Slice 2A — Set Up Your Business onboarding shell + Step 1.
// Welcome pre-step + Business & Trades. Steps 2/3/4 render as future
// steps in the progress bar with a "Coming next" card (no dead-end
// forms). Writes directly into Company Profile — the ONE source of
// truth. Draft persistence via localStorage keyed on the current
// user id. All contractor-facing strings centralized in
// ../lib/onboarding_i18n.js — English complete, Spanish stubs.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { ArrowUp, ArrowDown, Trash2, Upload, CheckCircle2, Circle, ArrowLeft, ArrowRight, PlayCircle } from "lucide-react";
import TradeSetupPanel from "@/components/tradesetup/TradeSetupPanel";
import { resolveSetupDestination, firstInProgressTrade } from "@/lib/setupProgress";
import Step3DocumentDefaults from "@/pages/Step3DocumentDefaults";
import KeyTeamMembersStep from "@/components/onboarding/KeyTeamMembersStep";
import {
  validateField as validateProfileField,
  mapServerFieldErrors as mapProfileServerFieldErrors,
} from "@/lib/placeholderValidation";

const STEPS = [
  { key: "business_and_trades", label: "Business & Trades" },
  { key: "trade_setup", label: "Trade Setup" },
  { key: "documents_and_customer_defaults", label: "Documents & Customer Defaults" },
  { key: "key_team_members", label: "Key Team Members" },
  { key: "review_and_ready", label: "Review & Ready" },
];

const PHASE1_TRADES = [
  "Roofing", "Flooring", "Stucco", "Painting", "Drywall",
  "General Contracting & Remodeling", "Restoration",
  "Concrete & Masonry", "Siding", "Gutters",
  "Handyman & General Repairs", "HVAC", "Fencing",
  "Decks & Patios", "Insulation",
];

// Small curated IANA list. Auto-detect via Intl API supplements this.
const TZ_LIST = [
  { iana: "America/New_York", label: "Eastern Time (New York)" },
  { iana: "America/Chicago", label: "Central Time (Chicago)" },
  { iana: "America/Denver", label: "Mountain Time (Denver)" },
  { iana: "America/Phoenix", label: "Mountain Time – no DST (Phoenix)" },
  { iana: "America/Los_Angeles", label: "Pacific Time (Los Angeles)" },
  { iana: "America/Anchorage", label: "Alaska Time" },
  { iana: "Pacific/Honolulu", label: "Hawaii Time" },
];

const draftKey = (uid) => `soyb-draft:${uid || "anon"}`;

// Slice 2C4 · Manual QA fix — the SoyB localStorage draft must NEVER
// override the server's authoritative trade state on hydration. Stale
// drafts (captured before a QA restoration or before the client-dirty
// tracking landed) previously resurrected damaged trade_categories /
// trade_setup_order / trade_setup_status / set_up_your_business_step
// values on refresh. These four keys are locked to the server response;
// all OTHER unsaved Company Profile draft keys (phone, email, address,
// company_name, website, license, time_zone, etc.) remain fully
// preserved.
const AUTHORITATIVE_SERVER_KEYS = Object.freeze([
  "trade_categories",
  "trade_setup_order",
  "trade_setup_status",
  "set_up_your_business_step",
]);

const stripAuthoritativeKeys = (draft) => {
  if (!draft || typeof draft !== "object") return draft;
  const clean = { ...draft };
  let changed = false;
  for (const k of AUTHORITATIVE_SERVER_KEYS) {
    if (k in clean) { delete clean[k]; changed = true; }
  }
  return { clean, changed };
};

const emptyForm = {
  company_name: "", dba: "", phone: "", email: "", website: "",
  address: { street: "", unit: "", city: "", state: "", zip: "" },
  license_number: "", license_state: "", license_expires: "",
  ein: "",
  service_area_notes: "", time_zone: "",
  trade_categories: [], trade_setup_order: [], trade_setup_status: {},
  // Phase 5B · Optional Primary Operations Contact — one work-order
  // convenience recipient. Empty envelope = unset.
  primary_operations_contact: { name: "", email: "", phone: "", label: "" },
};

const SetUpYourBusiness = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useLang();
  const [me, setMe] = useState(null);
  const [state, setState] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [logo, setLogo] = useState({ present: false, url: "" });
  const [saveStatus, setSaveStatus] = useState("idle"); // idle|saving|saved|failed
  const [errors, setErrors] = useState({});
  const [showWelcome, setShowWelcome] = useState(true);
  const bootRef = useRef(false);
  // Slice 2C6 · Review & Ready — local Finish-setup busy latch. No new
  // persistence: reuses the existing /pricing-profile/setup-progress
  // endpoint to record the contractor's explicit "Finish setup" press.
  const [finishBusy, setFinishBusy] = useState(false);

  // Slice 2C3 correction — expose a state refetch so Step 3 can request
  // an immediate `/onboarding/set-up-your-business/state` refresh after
  // a successful family status write. Must live at the TOP of the
  // component (before any conditional return) to comply with React
  // hook ordering rules.
  const refreshProgress = useCallback(async () => {
    try {
      const stR = await api.get("/onboarding/set-up-your-business/state");
      setState(stR.data);
    } catch { /* silent — Step 3 has its own toast surface */ }
  }, []);

  const activeStep = params.get("step") || "welcome";
  const editHref = (step, family) => {
    const base = `/app/setup?step=${step}`;
    return family ? `${base}&family=${family}` : base;
  };
  // Slice 2B1 · Canonical setup-progress resolver — when the contractor
  // lands on `/app/setup` with no explicit `?step=` (i.e. via "Resume
  // Setup" from the dashboard) AND Step 1 is already complete on the
  // backend, auto-route to the first unfinished trade in Trade Setup.
  // Only runs once after boot to avoid resolver ping-pong.
  const resolverRanRef = useRef(false);
  useEffect(() => {
    if (resolverRanRef.current) return;
    if (!state || !form) return;
    // Only resolve when the URL has no explicit step (welcome pre-step
    // OR bare `/app/setup`) AND Step 1 is complete AND the user has
    // already dismissed the Welcome pre-step (has a company_name).
    if (activeStep !== "welcome") return;
    if (!state?.steps?.[0]?.complete) return;
    if (!(form.company_name || "").trim()) return;
    resolverRanRef.current = true;
    const dest = resolveSetupDestination(state, form);
    if (dest.path && dest.path !== "/app/setup?step=welcome") {
      navigate(dest.path, { replace: true });
    }
  }, [state, form, activeStep, navigate]);

  // ---- Boot: load profile + state + draft ----
  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;
    (async () => {
      try {
        const [meR, cpR, stR] = await Promise.all([
          api.get("/auth/me"),
          api.get("/company-profile"),
          api.get("/onboarding/set-up-your-business/state"),
        ]);
        setMe(meR.data);
        setState(stR.data);
        // Merge server profile with localStorage draft (draft wins for
        // unsaved changes, server wins for saved values on first load).
        // Slice 2C4 · Manual QA fix — a stale draft must NEVER override
        // the server's authoritative trade state. Strip the four locked
        // authoritative keys from the draft BEFORE the merge, and rewrite
        // the sanitized draft back to localStorage so the stale values
        // cannot resurrect themselves on the next refresh. Non-trade
        // Company Profile draft keys (phone/email/address/company_name/
        // website/license/time_zone/etc.) remain fully preserved.
        const draftRaw = localStorage.getItem(draftKey(meR.data?.id));
        let parsedDraft = null;
        try { parsedDraft = draftRaw ? JSON.parse(draftRaw) : null; }
        catch (_e) { parsedDraft = null; }
        const { clean: sanitizedDraft, changed: draftHadStaleAuthKeys } =
          stripAuthoritativeKeys(parsedDraft) || { clean: null, changed: false };
        if (draftHadStaleAuthKeys && meR.data?.id) {
          try {
            if (sanitizedDraft && Object.keys(sanitizedDraft).length > 0) {
              localStorage.setItem(
                draftKey(meR.data.id), JSON.stringify(sanitizedDraft));
            } else {
              localStorage.removeItem(draftKey(meR.data.id));
            }
          } catch (_e) { /* ignore quota */ }
        }
        const base = {
          ...emptyForm,
          ...cpR.data,
          address: { ...emptyForm.address, ...(cpR.data.address || {}) },
          trade_setup_order: cpR.data.trade_setup_order && cpR.data.trade_setup_order.length
            ? cpR.data.trade_setup_order
            : (cpR.data.trade_categories || []),
          trade_setup_status: cpR.data.trade_setup_status || {},
          primary_operations_contact: {
            ...emptyForm.primary_operations_contact,
            ...(cpR.data.primary_operations_contact || {}),
          },
          // Phase 5B · Hydrate language selectors from the identity doc.
          app_language: meR?.data?.app_language || "",
          customer_document_language: meR?.data?.customer_document_language || "",
        };
        setForm(sanitizedDraft
          ? { ...base, ...sanitizedDraft,
              address: { ...base.address, ...(sanitizedDraft.address || {}) } }
          : base);
        setLogo({ present: !!cpR.data.logo_storage_path, url: cpR.data.logo_url || "" });
        // Skip welcome for accounts that already have a company name.
        if ((cpR.data.company_name || "").trim()) setShowWelcome(false);
      } catch (e) {
        console.error("Boot failed", e);
      }
    })();
  }, []);

  // ---- Draft persistence ----
  useEffect(() => {
    if (!me?.id) return;
    // Slice 2C4 · Manual QA fix — never persist the four authoritative
    // trade fields into localStorage. Only the server may re-hydrate
    // them. This keeps the draft strictly scoped to unsaved Company
    // Profile edits (phone / email / address / company_name / website /
    // license / time_zone / etc.).
    const persistable = { ...form };
    for (const k of AUTHORITATIVE_SERVER_KEYS) delete persistable[k];
    try { localStorage.setItem(draftKey(me.id), JSON.stringify(persistable)); } catch (_e) { /* ignore quota */ }
  }, [form, me?.id]);

  // ---- Time-zone auto-detect (only when saved value is blank) ----
  useEffect(() => {
    if (form.time_zone) return;
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detected) setForm((f) => ({ ...f, time_zone: detected }));
    } catch (_e) { /* Intl not available */ }
  }, [form.time_zone === ""]);

  // ---- Helpers ----
  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Slice 2C4 · Editing the affected value clears its inline error.
    setErrors((prev) => {
      if (!prev || !prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };
  // Slice 2C4 · Explicit dirty-tracking for trade fields. When false,
  // the Save handler omits trade_categories / trade_setup_order /
  // trade_setup_status from the PUT payload so a phone/email-only
  // correction cannot overwrite, reorder, or reset persisted trade
  // state. Any explicit trade edit (toggle / reorder / status change)
  // flips it to true.
  const [tradesDirty, setTradesDirty] = useState(false);

  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));
  // Phase 5B · Primary Operations Contact editor. Dirty-tracked so
  // unrelated saves don't blank the persisted envelope.
  const [pocDirty, setPocDirty] = useState(false);
  const setPoc = (k, v) => {
    setPocDirty(true);
    setForm((f) => ({
      ...f,
      primary_operations_contact: {
        ...(f.primary_operations_contact || { name: "", email: "", phone: "", label: "" }),
        [k]: v,
      },
    }));
    setErrors((prev) => {
      const key = `primary_operations_contact.${k}`;
      if (!prev || !prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };
  const toggleTrade = (tr) => {
    setTradesDirty(true);
    setForm((f) => {
      const has = f.trade_categories.includes(tr);
      const next = has ? f.trade_categories.filter((x) => x !== tr) : [...f.trade_categories, tr];
      const order = has
        ? f.trade_setup_order.filter((x) => x !== tr)
        : (f.trade_setup_order.includes(tr) ? f.trade_setup_order : [...f.trade_setup_order, tr]);
      const status = { ...f.trade_setup_status };
      if (has) delete status[tr];
      else status[tr] = status[tr] || "in_progress";
      return { ...f, trade_categories: next, trade_setup_order: order, trade_setup_status: status };
    });
  };
  const moveTrade = (tr, dir) => {
    setTradesDirty(true);
    setForm((f) => {
      const order = [...f.trade_setup_order];
      const i = order.indexOf(tr);
      if (i < 0) return f;
      const j = i + dir;
      if (j < 0 || j >= order.length) return f;
      [order[i], order[j]] = [order[j], order[i]];
      return { ...f, trade_setup_order: order };
    });
  };
  const setTradeStatus = (tr, s) => {
    setTradesDirty(true);
    setForm((f) => ({ ...f, trade_setup_status: { ...f.trade_setup_status, [tr]: s } }));
  };

  const validate = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = t("errors.business_name_required");
    if (!form.phone.trim()) e.phone = t("errors.business_phone_required");
    if (!form.email.trim()) e.email = t("errors.business_email_required");
    if (!form.address.zip.trim()) e.zip = t("errors.business_zip_required");
    if (form.trade_categories.length === 0) e.trades = t("errors.at_least_one_trade");
    // Slice 2C4 · Client-side placeholder / fake-contact protection for
    // customer-facing profile fields. Only checks non-empty values —
    // required-field messages above still handle blanks. Real business
    // names, phones, emails, and websites pass unchanged.
    const profileKinds = {
      company_name: "business", phone: "phone", email: "email", website: "website",
    };
    for (const k of Object.keys(profileKinds)) {
      const msg = validateProfileField(profileKinds[k], form[k]);
      if (msg) e[k] = msg;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Slice 2C4 · Live derivation of "current form has blocking errors"
  // from the SAME rules validate() runs at Save time — no duplicate
  // completion rule. This is what hides the Step-1-complete banner /
  // Continue-to-Trade-Setup button while the current visible form
  // contains blocking placeholder / fake-contact / required-blank
  // errors, even when the server still thinks Step 1 is complete
  // (because the previously-saved values were valid).
  const currentFormBlocked = useMemo(() => {
    if (!form.company_name || !form.company_name.trim()) return true;
    if (!form.phone || !form.phone.trim()) return true;
    if (!form.email || !form.email.trim()) return true;
    if (!form.address || !form.address.zip || !form.address.zip.trim()) return true;
    if (!form.trade_categories || form.trade_categories.length === 0) return true;
    const profileKinds = {
      company_name: "business", phone: "phone", email: "email", website: "website",
    };
    for (const k of Object.keys(profileKinds)) {
      if (validateProfileField(profileKinds[k], form[k])) return true;
    }
    return false;
  }, [form]);

  const save = async () => {
    if (!validate()) return;
    setSaveStatus("saving");
    try {
      // Slice 2C4 · Contact-only save must not overwrite persisted
      // trades. Strip the three trade fields from the form and add
      // them back ONLY when the contractor explicitly edited them
      // during this interaction. `set_up_your_business_step` is
      // omitted so the server's monotonic guard preserves the
      // persisted pointer (never regress from Step 3 to Step 1).
      const { trade_categories, trade_setup_order, trade_setup_status,
              set_up_your_business_step: _drop,
              primary_operations_contact: _pocForm,
              app_language: _al, customer_document_language: _cdl,
              ...profileOnly } = form;
      const payload = { ...profileOnly };
      if (tradesDirty) {
        const status = { ...(trade_setup_status || {}) };
        for (const tr of (trade_categories || [])) {
          if (!status[tr]) status[tr] = "in_progress";
        }
        payload.trade_categories = trade_categories || [];
        payload.trade_setup_order = trade_setup_order && trade_setup_order.length
          ? trade_setup_order
          : [...(trade_categories || [])];
        payload.trade_setup_status = status;
      }
      // Phase 5B · Only send Primary Operations Contact when it was
      // edited in this session — server blank-preserves an unsent
      // envelope so unrelated saves cannot silently erase it.
      if (pocDirty) {
        payload.primary_operations_contact = _pocForm || {
          name: "", email: "", phone: "", label: "",
        };
      }
      const putR = await api.put("/company-profile", payload);
      setSaveStatus("saved");
      setTradesDirty(false);
      setPocDirty(false);
      try { localStorage.removeItem(draftKey(me?.id)); } catch (_e) { /* ignore */ }
      const stR = await api.get("/onboarding/set-up-your-business/state");
      setState(stR.data);
      // Slice 2B1 · Canonical Save-and-Continue.
      // If Step 1 is now complete on the backend, route the contractor
      // to the next incomplete step (usually the first in-progress
      // trade inside Trade Setup). The green "Continue to Trade Setup"
      // banner remains as a manual fallback for anyone who bails on
      // the auto-navigation (e.g. clicks Save then immediately clicks
      // another link before this navigate fires).
      const step1DoneNow = stR.data?.steps?.[0]?.complete;
      if (step1DoneNow) {
        const merged = { ...form, ...(putR?.data || {}) };
        const dest = resolveSetupDestination(stR.data, merged);
        toast.success(t("saved"));
        navigate(dest.path);
        return;
      }
      toast.success(t("saved"));
    } catch (err) {
      console.error("Save failed", err);
      // Slice 2C4 · Map atomic placeholder rejection into per-field errors
      // so the contractor can correct them without a generic banner.
      const serverPh = mapProfileServerFieldErrors(err);
      if (Object.keys(serverPh).length > 0) {
        setErrors((prev) => ({ ...prev, ...serverPh }));
        setSaveStatus("failed");
        toast.error(t("save_failed"));
        return;
      }
      setSaveStatus("failed");
      toast.error(t("save_failed"));
    }
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/company-profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo({ present: true, url: data.logo_url || "" });
      toast.success(t("soyb.toast_logo_uploaded"));
    } catch { toast.error(t("soyb.toast_logo_upload_failed")); }
  };
  const removeLogo = async () => {
    if (!window.confirm(t("logo_remove_confirm"))) return;
    try {
      await api.delete("/company-profile/logo");
      setLogo({ present: false, url: "" });
    } catch { toast.error(t("soyb.toast_logo_remove_failed")); }
  };

  const completedCount = state?.completed_count ?? 0;
  const totalCount = state?.total_count ?? 4;
  // Slice 2C4 · Genuine per-step effective completion. Business & Trades
  // (STEPS index 0) is complete only when the server flag is true AND
  // the current unsaved form has no blocking validation errors. Every
  // other step retains its server-reported completion. Both the top
  // "X of Y" label AND each stepper pill derive from this SAME array —
  // one source of truth, no second competing rule and no numeric-index
  // inference (which is wrong when a middle step is blocked).
  const effectiveStepComplete = STEPS.map((s, ix) => {
    const serverComplete = !!state?.steps?.[ix]?.complete;
    if (s.key === "business_and_trades") return serverComplete && !currentFormBlocked;
    return serverComplete;
  });
  const effectiveCompletedCount = effectiveStepComplete.filter(Boolean).length;

  // ============ WELCOME PRE-STEP ============
  if (activeStep === "welcome" && showWelcome) {
    const firstName = (me?.full_name || "").split(" ")[0] || "";
    return (
      <DashboardLayout title={t("brand")} subtitle={t("brand_tagline")}>
        <div data-testid="soyb-welcome" className="max-w-2xl space-y-4">
          <div className="jc-card p-6 space-y-3">
            <div className="text-sm text-[var(--jc-text-soft)]">{t("welcome_hi")}{firstName ? `, ${firstName}` : ""}</div>
            {form.company_name && (
              <div className="text-xs text-[var(--jc-text-soft)]" data-testid="soyb-active-company">
                {t("welcome_active_company")}: <span className="font-semibold text-[var(--jc-text)]">{form.company_name}</span>
              </div>
            )}
            <div className="text-sm">{t("welcome_body_intro")}</div>
            <ul className="text-sm list-disc pl-5 space-y-1 text-[var(--jc-text-soft)]">
              {t("welcome_bullets").map((b) => <li key={b}>{b}</li>)}
            </ul>
            <div className="pt-2 flex gap-2">
              <button
                data-testid="soyb-welcome-continue"
                onClick={() => { setShowWelcome(false); navigate("/app/setup?step=business_and_trades"); }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary"
              >
                {t("welcome_continue")} <ArrowRight size={14} />
              </button>
              <button
                onClick={() => navigate("/app")}
                className="px-3 py-2 text-sm rounded-md border border-[var(--jc-border-strong)]"
              >{t("back_to_dashboard")}</button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ============ PROGRESS BAR ============
  const progressBar = (
    <div className="jc-card p-3" data-testid="soyb-progress">
      <div className="text-xs text-[var(--jc-text-soft)] mb-1">
        {t("progress_label", effectiveCompletedCount, totalCount)}
      </div>
      <ol className="flex flex-wrap gap-2 text-xs">
        {STEPS.map((s, ix) => {
          // Slice 2C4 · Each pill derives from its OWN effective
          // completion boolean — never from the numeric position. The
          // "X of Y" label above uses the same array.
          const done = effectiveStepComplete[ix];
          const current = s.key === (state?.current_step_key || "business_and_trades");
          return (
            <li key={s.key} data-testid={`soyb-step-${s.key}`}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded ${current ? "bg-[var(--jc-navy)] text-white" : done ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}`}>
              {done ? <CheckCircle2 size={12} /> : <Circle size={12} />}
              {t(`step_labels.${s.key}`) || s.label}
            </li>
          );
        })}
      </ol>
    </div>
  );

  // ============ STEP 2 · Trade Setup (Slice 2B1) ============
  if (activeStep === "trade_setup") {
    return (
      <DashboardLayout title={t("brand")}
                        subtitle="Set up how you price and build each type of work. You can adjust it anytime.">
        <div className="max-w-3xl space-y-4" data-testid="soyb-step2">
          {progressBar}
          <TradeSetupPanel />
          <div className="pt-1">
            <button onClick={() => navigate("/app")}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                    data-testid="soyb-step2-back-dashboard">
              <ArrowLeft size={12} /> Back to Dashboard
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ============ STEP 3 · Documents & Customer Defaults (Slice 2C3) ============
  if (activeStep === "documents_and_customer_defaults") {
    return (
      <DashboardLayout title={t("brand")}>
        <div className="max-w-3xl space-y-4" data-testid="soyb-step3">
          {progressBar}
          <Step3DocumentDefaults onProgressRefresh={refreshProgress} />
        </div>
      </DashboardLayout>
    );
  }

  // ===== STEP · Key Team Members (optional) · PO 2026-02 =====
  // Additive optional step. Skippable for solo contractors. Writes
  // each row to POST /api/assignees (kind='individual') via the
  // existing canonical db.assignees owner. No side effects.
  if (activeStep === "key_team_members") {
    return (
      <DashboardLayout title={t("brand")}>
        <div className="max-w-3xl space-y-4" data-testid="soyb-key-team-members">
          {progressBar}
          <KeyTeamMembersStep
            onDone={() => navigate(editHref("review_and_ready"))}
          />
        </div>
      </DashboardLayout>
    );
  }

  // ============ STEP 4 · Review & Ready (Slice 2C6) ============
  if (activeStep === "review_and_ready") {
    // Read-only summary — no new setup persistence, no new endpoints,
    // no new collections. All data is sourced from the already-fetched
    // /onboarding/set-up-your-business/state + /company-profile.
    const step1 = state?.steps?.[0] || {};
    const step2 = state?.steps?.[1] || {};
    const step3 = state?.steps?.[2] || {};
    const step4 = state?.steps?.[3] || {};
    const fs = step3.family_statuses || {};
    const fv = step3.family_validation || {};
    const _pill = (label, tid, cls) => (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
        data-testid={tid}>{label}</span>
    );
    const familyPill = (famKey) => {
      const st = fs[famKey];
      // Slice 2C6 · Checkpoint 1 fix — `family_validation` returns an
      // OBJECT per family (empty `{}` when no issues, `{field: reason}`
      // when blocked). `!!{}` is truthy in JS so the previous check
      // marked EVERY family as "Needs attention" regardless of real
      // saved state. Count the actual entries instead.
      const issueMap = fv[famKey] || {};
      const hasIssue = typeof issueMap === "object"
        && !Array.isArray(issueMap)
        && Object.keys(issueMap).length > 0;
      if (hasIssue) {
        return _pill(t("review_pill_needs_attention"),
          `soyb-review-${famKey}-status-needs-attention`,
          "bg-amber-100 text-amber-900");
      }
      if (st === "later") {
        return _pill(t("review_pill_set_later"),
          `soyb-review-${famKey}-status-later`,
          "bg-slate-100 text-slate-800");
      }
      if (st === "ready") {
        return _pill(t("review_pill_configured"),
          `soyb-review-${famKey}-status-configured`,
          "bg-emerald-100 text-emerald-900");
      }
      return _pill(t("review_pill_needs_attention"),
        `soyb-review-${famKey}-status-needs-attention`,
        "bg-amber-100 text-amber-900");
    };
    const cpPill = step1.complete
      ? _pill(t("review_pill_configured"), "soyb-review-company-status-configured",
              "bg-emerald-100 text-emerald-900")
      : _pill(t("review_pill_needs_attention"), "soyb-review-company-status-needs-attention",
              "bg-amber-100 text-amber-900");
    const tradesPill = step2.complete
      ? _pill(t("review_pill_configured"), "soyb-review-trades-status-configured",
              "bg-emerald-100 text-emerald-900")
      : _pill(t("review_pill_needs_attention"), "soyb-review-trades-status-needs-attention",
              "bg-amber-100 text-amber-900");
    const gotoFamily = (family) =>
      navigate(editHref("documents_and_customer_defaults", family));
    const canFinish = !!step3.complete && !finishBusy;
    const doFinishSetup = async () => {
      if (!canFinish || finishBusy) return;
      setFinishBusy(true);
      try {
        // Reuse the existing pricing-profile setup-progress endpoint;
        // no new persistence surface introduced by Slice 2C6.
        await api.patch("/pricing-profile/setup-progress",
          { step: 4, completed: true, section: "review" });
        await refreshProgress();
        navigate("/app");
      } catch (e) {
        toast.error(t("soyb.toast_finish_setup_failed"));
      } finally {
        setFinishBusy(false);
      }
    };
    const doContinueLater = () => navigate("/app");
    const familyRow = (famKey, title) => (
      <div key={famKey}
           className="flex items-center justify-between rounded-md border p-3"
           data-testid={`soyb-review-family-${famKey}`}>
        <div className="space-y-1">
          <div className="text-sm font-semibold" data-testid={`soyb-review-family-${famKey}-title`}>
            {title}
          </div>
          <div>{familyPill(famKey)}</div>
        </div>
        <button
          type="button"
          onClick={() => gotoFamily(famKey)}
          data-testid={`soyb-review-family-${famKey}-edit`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]">
          {t("review_edit")}
        </button>
      </div>
    );
    const tradesList = Array.isArray(form?.trade_categories)
      ? form.trade_categories : [];
    return (
      <DashboardLayout title={t("brand")}>
        <div className="max-w-3xl space-y-4" data-testid="soyb-step4">
          {progressBar}
          <div className="jc-card p-5 space-y-2" data-testid="soyb-review-header">
            <div className="text-lg font-semibold" data-testid="soyb-review-title">
              {t("review_title")}
            </div>
            <div className="text-sm text-[var(--jc-text-soft)]">
              {t("review_intro")}
            </div>
          </div>

          <div className="jc-card p-5 space-y-3"
               data-testid="soyb-review-section-company">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold" data-testid="soyb-review-company-title">{t("review_section_company")}</div>
                <div className="text-xs text-[var(--jc-text-soft)]"
                     data-testid="soyb-review-company-summary">
                  {form?.company_name || t("review_company_no_name")}
                  {form?.phone ? ` · ${form.phone}` : ""}
                  {form?.email ? ` · ${form.email}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {cpPill}
                <button
                  type="button"
                  onClick={() => navigate(editHref("business_and_trades"))}
                  data-testid="soyb-review-company-edit"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]">
                  {t("review_edit")}
                </button>
              </div>
            </div>
          </div>

          <div className="jc-card p-5 space-y-3"
               data-testid="soyb-review-section-trades">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold" data-testid="soyb-review-trades-title">{t("review_section_trades")}</div>
                <div className="text-xs text-[var(--jc-text-soft)]"
                     data-testid="soyb-review-trades-summary">
                  {tradesList.length
                    ? tradesList.join(", ")
                    : t("review_trades_none")}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {tradesPill}
                <button
                  type="button"
                  onClick={() => navigate(editHref("trade_setup"))}
                  data-testid="soyb-review-trades-edit"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]">
                  {t("review_edit")}
                </button>
              </div>
            </div>
          </div>

          <div className="jc-card p-5 space-y-3"
               data-testid="soyb-review-section-documents">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold" data-testid="soyb-review-documents-title">
                {t("review_section_documents")}
              </div>
            </div>
            <div className="space-y-2">
              {familyRow("proposal",        t("family_names.proposal"))}
              {familyRow("contract",        t("family_names.contract"))}
              {familyRow("invoice",         t("family_names.invoice"))}
              {familyRow("customer_update", t("family_names.customer_update"))}
              {familyRow("closeout",        t("family_names.closeout"))}
              {familyRow("warranty",        t("family_names.warranty"))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2"
               data-testid="soyb-review-actions">
            <div className="text-xs text-[var(--jc-text-soft)]"
                 data-testid="soyb-review-finish-hint">
              {step4.complete
                ? t("review_hint_complete")
                : (canFinish
                    ? t("review_hint_ready")
                    : t("review_hint_blocked"))}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={doContinueLater}
                data-testid="soyb-review-continue-later"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]">
                {t("review_continue_later")}
              </button>
              <button
                type="button"
                disabled={!canFinish}
                onClick={doFinishSetup}
                data-testid="soyb-review-finish-setup"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                {t("review_finish_setup")}
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ============ FUTURE STEPS fallback ============
  if (activeStep !== "business_and_trades" && activeStep !== "welcome") {
    const label = t(`step_labels.${activeStep}`) || STEPS.find((s) => s.key === activeStep)?.label || t("soyb.future_step_default_label");
    return (
      <DashboardLayout title={t("brand")}>
        <div className="max-w-2xl space-y-4" data-testid="soyb-future-step">
          {progressBar}
          <div className="jc-card p-6 space-y-2">
            <div className="text-lg font-semibold">{t("future_step_title")}</div>
            <div className="text-sm text-[var(--jc-text-soft)]">{t("future_step_body", label)}</div>
            <div className="pt-2">
              <button onClick={() => navigate("/app")}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary"
                      data-testid="soyb-future-back-dashboard">
                <ArrowLeft size={14} /> {t("back_to_dashboard")}
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ============ STEP 1 ============
  return (
    <DashboardLayout title={t("brand")} subtitle={t("step1_subtitle")}>
      <div data-testid="soyb-step1" className="max-w-3xl space-y-4">
        {progressBar}
        {state?.steps?.[0]?.complete && !currentFormBlocked && (
          <div className="jc-card p-3 border-l-4 border-l-green-500 bg-green-50/40 flex items-center justify-between gap-3"
               data-testid="soyb-step1-complete-banner">
            <div className="text-sm font-semibold text-green-900">
              {t("step1_complete_banner")}
            </div>
            <button
              type="button"
              onClick={() => navigate("/app/setup?step=trade_setup")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
              data-testid="soyb-continue-to-trade-setup"
            >
              {t("soyb.continue_to_trade_setup")} →
            </button>
          </div>
        )}

        {/* Slice 2C4 · Renders in place of the Step-1-complete banner
            whenever the CURRENT visible Business & Trades form contains
            blocking validation errors — even when the server still
            regards the previously-saved profile as complete. Continue
            to Trade Setup is NOT rendered while blocked (not merely
            disabled). Fixing the affected fields and successfully
            saving restores the completion banner immediately. */}
        {state?.steps?.[0]?.complete && currentFormBlocked && (
          <div className="jc-card p-3 border-l-4 border-l-red-500 bg-red-50 flex items-start gap-3"
               data-testid="soyb-step1-needs-attention">
            <div className="text-sm font-semibold text-red-900">
              {t("soyb.step1_blocked_title")}
            </div>
            <p className="text-xs text-red-900/80">
              {t("soyb.step1_blocked_body")}
            </p>
          </div>
        )}

        {/* Business info */}
        <section className="jc-card p-4 space-y-3" data-testid="soyb-business">
          <div className="text-sm font-semibold">{t("section_business")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t("labels.business_name")} required error={errors.company_name}>
              <input data-testid="soyb-business-name"
                     value={form.company_name} onChange={(e) => setField("company_name", e.target.value)}
                     className="jc-input" />
            </Field>
            <Field label={t("labels.business_phone")} required error={errors.phone}>
              <input data-testid="soyb-business-phone"
                     value={form.phone} onChange={(e) => setField("phone", e.target.value)}
                     className="jc-input" />
            </Field>
            <Field label={t("labels.business_email")} required error={errors.email}>
              <input type="email" data-testid="soyb-business-email"
                     value={form.email} onChange={(e) => setField("email", e.target.value)}
                     className="jc-input" />
            </Field>
            <Field label={t("labels.business_zip")} required error={errors.zip}>
              <input data-testid="soyb-business-zip"
                     value={form.address.zip} onChange={(e) => setAddr("zip", e.target.value)}
                     className="jc-input" />
            </Field>
            <Field label={t("labels.business_address")}>
              <input data-testid="soyb-business-street"
                     value={form.address.street} onChange={(e) => setAddr("street", e.target.value)}
                     className="jc-input" placeholder={t("soyb.placeholder_street")} />
            </Field>
            <Field label={t("labels.business_city")}>
              <input data-testid="soyb-business-city"
                     value={form.address.city} onChange={(e) => setAddr("city", e.target.value)}
                     className="jc-input" />
            </Field>
            <Field label={t("labels.business_state")}>
              <input data-testid="soyb-business-state"
                     value={form.address.state} onChange={(e) => setAddr("state", e.target.value)}
                     className="jc-input" />
            </Field>
            <Field label={t("labels.website")} error={errors.website}>
              <input data-testid="soyb-website"
                     value={form.website} onChange={(e) => setField("website", e.target.value)}
                     className="jc-input" placeholder="https://…" />
            </Field>
            <Field label={t("labels.service_area")}>
              <input data-testid="soyb-service-area"
                     value={form.service_area_notes} onChange={(e) => setField("service_area_notes", e.target.value)}
                     className="jc-input" />
            </Field>
            <Field label={t("labels.license")}>
              <input data-testid="soyb-license"
                     value={form.license_number} onChange={(e) => setField("license_number", e.target.value)}
                     className="jc-input" />
            </Field>
            <Field label="EIN (Federal Tax ID)" error={errors.ein}>
              <input data-testid="soyb-ein"
                     value={form.ein} onChange={(e) => setField("ein", e.target.value)}
                     className="jc-input"
                     placeholder="XX-XXXXXXX"
                     maxLength={11} />
              <span className="block text-[11px] text-[var(--jc-text-soft)] mt-0.5">
                Optional. Used for 1099s, insurance, payments, and Business Texting when you go paid.
              </span>
            </Field>
          </div>
        </section>

        {/* Time zone */}
        <section className="jc-card p-4 space-y-2" data-testid="soyb-timezone">
          <div className="text-sm font-semibold">{t("section_time_zone")}</div>
          <select data-testid="soyb-timezone-select"
                  value={form.time_zone}
                  onChange={(e) => setField("time_zone", e.target.value)}
                  className="jc-input md:w-96">
            <option value="">—</option>
            {TZ_LIST.map((z) => <option key={z.iana} value={z.iana}>{z.label}</option>)}
            {form.time_zone && !TZ_LIST.find((z) => z.iana === form.time_zone) && (
              <option value={form.time_zone}>{form.time_zone}</option>
            )}
          </select>
        </section>

        {/* Logo */}
        <section className="jc-card p-4 space-y-2" data-testid="soyb-logo">
          <div className="text-sm font-semibold">{t("section_logo")}</div>
          <div className="text-xs text-[var(--jc-text-soft)]">{t("logo_types")}</div>
          <div className="flex items-center gap-3">
            {logo.present && logo.url && (
              <img src={logo.url} alt="logo" className="w-16 h-16 object-contain border border-[var(--jc-border)] rounded" />
            )}
            <label className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded border border-[var(--jc-border-strong)] cursor-pointer">
              <Upload size={12} /> {logo.present ? t("logo_replace") : t("soyb.logo_upload")}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                     className="hidden" data-testid="soyb-logo-input"
                     onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
            </label>
            {logo.present && (
              <button onClick={removeLogo} data-testid="soyb-logo-remove"
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded border border-[var(--jc-border-strong)]">
                <Trash2 size={12} /> {t("logo_remove")}
              </button>
            )}
          </div>
        </section>

        {/* Trades */}
        <section className="jc-card p-4 space-y-3" data-testid="soyb-trades">
          <div className="text-sm font-semibold">{t("section_trades")}</div>
          <div className="text-xs text-[var(--jc-text-soft)]">{t("trades_hint")}</div>
          {errors.trades && <div className="text-xs text-red-700" data-testid="soyb-trades-error">{errors.trades}</div>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {PHASE1_TRADES.map((tr) => {
              const on = form.trade_categories.includes(tr);
              return (
                <button
                  key={tr}
                  onClick={() => toggleTrade(tr)}
                  data-testid={`soyb-trade-${tr.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                  className={`text-left text-xs font-semibold px-3 py-2 rounded border ${on ? "bg-[var(--jc-navy)] text-white border-[var(--jc-navy)]" : "bg-white border-[var(--jc-border-strong)]"}`}
                >
                  {on ? "✓ " : ""}{tr}
                </button>
              );
            })}
          </div>
        </section>

        {/* Trade order */}
        {form.trade_categories.length > 0 && (
          <section className="jc-card p-4 space-y-2" data-testid="soyb-trade-order">
            <div className="text-sm font-semibold">{t("section_trade_order")}</div>
            <div className="text-xs text-[var(--jc-text-soft)]">{t("section_trade_order_help")}</div>
            <ol className="space-y-1.5">
              {form.trade_setup_order.map((tr, i) => {
                const currentStatus = (form.trade_setup_status[tr] || "in_progress").toLowerCase();
                // Slice 2B1 · Contextual per-trade action beside the
                // status dropdown. Status dropdown = change status.
                // Up/down arrows = re-order. This button = open that
                // specific trade in Trade Setup. All three controls
                // are visually distinct so the contractor can never
                // infer that an arrow or dropdown opens Trade Setup.
                const actionLabel =
                  currentStatus === "ready"    ? t("soyb.trade_action.edit_setup")     :
                  currentStatus === "later"    ? t("soyb.trade_action.resume_setup")   :
                                                 t("soyb.trade_action.continue_setup");
                const openThisTrade = async () => {
                  // For a `later` trade, reopen it first so the queue
                  // shows it as in_progress when Trade Setup loads.
                  // We use the trade-setup service endpoint so the
                  // server is the single source of truth for the
                  // reopen (and its audit stamp is cleared).
                  if (currentStatus === "later") {
                    try {
                      await api.post(`/onboarding/trade-setup/trade/${encodeURIComponent(tr)}/reopen`);
                      setForm((f) => ({
                        ...f,
                        trade_setup_status: { ...f.trade_setup_status, [tr]: "in_progress" },
                      }));
                    } catch (e) {
                      // Non-fatal: still navigate — the user can hit
                      // Reopen inside Trade Setup as a fallback.
                      // eslint-disable-next-line no-console
                      console.warn("reopen failed, continuing:", e);
                    }
                  }
                  navigate(`/app/setup?step=trade_setup&trade=${encodeURIComponent(tr)}`);
                };
                return (
                <li key={tr} data-testid={`soyb-order-row-${i}`}
                    data-trade={tr}
                    data-status={currentStatus}
                    className="flex items-center gap-2 text-sm">
                  <span className="w-6 text-xs text-[var(--jc-text-soft)]">{i + 1}.</span>
                  <span className="flex-1 font-medium">{tr}</span>
                  <select
                    data-testid={`soyb-status-${i}`}
                    value={form.trade_setup_status[tr] || "in_progress"}
                    onChange={(e) => setTradeStatus(tr, e.target.value)}
                    className="text-xs border border-[var(--jc-border-strong)] rounded px-1.5 py-0.5"
                    title={t("soyb.title_status_only")}
                  >
                    <option value="in_progress">{t("trade_status.in_progress")}</option>
                    <option value="later">{t("trade_status.later")}</option>
                  </select>
                  <button
                    type="button"
                    onClick={openThisTrade}
                    data-testid={`soyb-trade-action-${i}`}
                    data-action={actionLabel.toLowerCase().replace(/\s+/g, "-")}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded jc-btn-primary"
                    title={t("soyb.title_open_trade")}
                  >
                    <PlayCircle size={12} /> {actionLabel}
                  </button>
                  <button onClick={() => moveTrade(tr, -1)} disabled={i === 0}
                          data-testid={`soyb-move-up-${i}`}
                          title={t("soyb.title_move_up")}
                          className="p-1 rounded border border-[var(--jc-border-strong)] disabled:opacity-40">
                    <ArrowUp size={12} />
                  </button>
                  <button onClick={() => moveTrade(tr, 1)} disabled={i === form.trade_setup_order.length - 1}
                          data-testid={`soyb-move-down-${i}`}
                          title={t("soyb.title_move_down")}
                          className="p-1 rounded border border-[var(--jc-border-strong)] disabled:opacity-40">
                    <ArrowDown size={12} />
                  </button>
                </li>
                );
              })}
            </ol>
          </section>
        )}

        {/* Phase 5B · Optional language capture — inside Step 1 with
            explicit "you can change this later" hint. Writes go to the
            existing endpoints (PUT /auth/language and
            PUT /auth/customer-document-language) — no parallel storage. */}
        <section className="jc-card p-4 space-y-3" data-testid="soyb-language-section">
          <div className="text-sm font-semibold">Language <span className="text-xs font-normal text-[var(--jc-text-soft)]">(optional — you can change this anytime in Account settings)</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="App language">
              <select
                data-testid="soyb-app-language"
                value={form.app_language || ""}
                onChange={async (e) => {
                  const v = e.target.value;
                  setForm((f) => ({ ...f, app_language: v }));
                  if (!v) return;
                  try {
                    await api.put("/api/auth/language", { language: v });
                  } catch { /* silent — user can retry */ }
                }}
                className="jc-input"
              >
                <option value="">Use default (English)</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </Field>
            <Field label="Customer document language">
              <select
                data-testid="soyb-customer-doc-language"
                value={form.customer_document_language || ""}
                onChange={async (e) => {
                  const v = e.target.value;
                  setForm((f) => ({ ...f, customer_document_language: v }));
                  if (!v) return;
                  try {
                    await api.put("/api/auth/customer-document-language", { language: v });
                  } catch { /* silent — user can retry */ }
                }}
                className="jc-input"
              >
                <option value="">Use default (English)</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </Field>
          </div>
        </section>

        {/* Phase 5B · Primary Operations Contact (optional, single work-order
            recipient — not a crew management system). Empty = unset. */}
        <section className="jc-card p-4 space-y-3" data-testid="soyb-primary-operations-contact">
          <div className="text-sm font-semibold">Primary Operations Contact <span className="text-xs font-normal text-[var(--jc-text-soft)]">(optional)</span></div>
          <div className="text-xs text-[var(--jc-text-soft)]">
            One convenience contact for work-order delivery — such as a crew lead, foreman, or superintendent. Leave blank to send work orders to your business email.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Name" error={errors["primary_operations_contact.name"]}>
              <input
                data-testid="soyb-poc-name"
                value={form.primary_operations_contact?.name || ""}
                onChange={(e) => setPoc("name", e.target.value)}
                className="jc-input"
                placeholder="e.g. Sam Rivera"
              />
            </Field>
            <Field label="Email" error={errors["primary_operations_contact.email"]}>
              <input
                type="email"
                data-testid="soyb-poc-email"
                value={form.primary_operations_contact?.email || ""}
                onChange={(e) => setPoc("email", e.target.value)}
                className="jc-input"
                placeholder="crewlead@company.com"
              />
            </Field>
            <Field label="Phone (optional)" error={errors["primary_operations_contact.phone"]}>
              <input
                data-testid="soyb-poc-phone"
                value={form.primary_operations_contact?.phone || ""}
                onChange={(e) => setPoc("phone", e.target.value)}
                className="jc-input"
                placeholder="(555) 555-5555"
              />
            </Field>
            <Field label="Label (optional)" error={errors["primary_operations_contact.label"]}>
              <input
                data-testid="soyb-poc-label"
                value={form.primary_operations_contact?.label || ""}
                onChange={(e) => setPoc("label", e.target.value)}
                className="jc-input"
                placeholder="Crew Lead, Foreman, Superintendent"
                maxLength={40}
              />
            </Field>
          </div>
        </section>

        {/* Save bar */}
        <div className="flex items-center justify-between gap-2 pt-2 pb-6">          <button onClick={() => navigate("/app")}
                  data-testid="soyb-back-dashboard"
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded border border-[var(--jc-border-strong)]">
            <ArrowLeft size={14} /> {t("back_to_dashboard")}
          </button>
          <div className="flex items-center gap-2">
            {saveStatus === "saved" && <span className="text-xs text-green-700" data-testid="soyb-save-status">{t("saved")}</span>}
            {saveStatus === "failed" && <span className="text-xs text-red-700" data-testid="soyb-save-status">{t("save_failed")}</span>}
            <button onClick={save} disabled={saveStatus === "saving"}
                    data-testid="soyb-save"
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-50">
              {saveStatus === "saving" ? t("saving") : t("save")}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const Field = ({ label, required, error, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-[var(--jc-text-soft)]">
      {label} {required && <span className="text-red-600">*</span>}
    </span>
    {children}
    {error && <div className="text-xs text-red-700 mt-0.5">{error}</div>}
  </label>
);

export default SetUpYourBusiness;
